import { useMemo, useState, useCallback } from 'react';

import { useGetLocationAddressesQuery, LocationAddressFilterType } from '@/entities/location';

import type { SelectOption } from '@/shared/ui';
import { useDebounce, useProjectSiteParams } from '@/shared/lib';

interface UseLocationSelectParams {
  /** Resolved location-address filter type, or null/undefined to skip the query. */
  filterType?: LocationAddressFilterType | null;
  /** Currently selected option value (id), used to recognise the combobox's selection echo. */
  selectedValue?: string;
  /** Option pinned in the list even when missing from search results (e.g. an edit-mode value). */
  initialOption?: SelectOption;
}

/**
 * Shared model hook for the consignment location selects (starting point + service destination).
 *
 * Owns the location-address query, debounced search, option building, the optional pinned
 * "initial option" merge, and the search-echo guard. The cards stay presentational.
 *
 * Echo guard: the combobox echoes the selected option's (composite) label back through
 * onInputValueChange. Searching the backend by that label returns nothing (it matches by name),
 * which would drop the option and flicker the selected value away — so an incoming search equal to
 * the selected label is treated as an echo and ignored.
 */
export function useLocationSelect({ filterType, selectedValue, initialOption }: UseLocationSelectParams) {
  const { projectId, siteId } = useProjectSiteParams();
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 300);

  const { data: locationAddresses, isLoading } = useGetLocationAddressesQuery(
    {
      projectId: Number(projectId),
      siteId: Number(siteId),
      type: filterType!,
      search: debouncedSearchValue
    },
    {
      skip: !filterType || !projectId || !siteId
    }
  );

  const locationOptions: SelectOption[] = useMemo(() => {
    const base = (locationAddresses ?? []).map((location) => {
      const parts = [location.name];
      if (location.city) parts.push(location.city);
      if (location.country) parts.push(location.country);

      return {
        value: location.id,
        label: parts.join(', ')
      };
    });

    if (!initialOption || base.some((opt) => opt.value === initialOption.value)) return base;
    return [initialOption, ...base];
  }, [locationAddresses, initialOption]);

  const selectedLabel = useMemo(
    () => locationOptions.find((opt) => opt.value === selectedValue)?.label,
    [locationOptions, selectedValue]
  );

  const onSearch = useCallback(
    (text: string) => {
      if (text === selectedLabel) return; // ignore the combobox echoing the selected label
      setSearchValue(text);
    },
    [selectedLabel]
  );

  const resetSearch = useCallback(() => {
    setSearchValue('');
  }, []);

  return {
    locationOptions,
    isLoading,
    searchValue,
    setSearchValue,
    onSearch,
    resetSearch
  };
}
